import { VariableInputPicker } from "@/src/app/documents/variableInputPicker";
import { Input } from "@/src/components/universalInput";
import {
  ConditionGroup,
  DocumentCondition,
  DocumentVariable,
  DocumentVariableFormat,
  EntryMode,
  ExitMode,
  FieldsSection,
  ManualVariable,
  Patient,
  PatientDocument,
  StatisticsReport,
  User,
  conditionOperators,
  documentVariableFormats,
  entryModes,
  exitModes,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  decodeAndParseBase64,
  encodeBase64,
  getItemId,
  jsonAbstract,
  uid,
} from "@/src/utils/utils";
import { useState } from "react";
import { getManualVariableValue } from "./manualValuesVariables";

function replaceVariablesWithValues({
  patientDocument,
  content,
  isPlaceholder,
}: {
  patientDocument: PatientDocument | null | undefined;
  content: string;
  isPlaceholder?: boolean;
}): string {
  content ||= "";
  content = content.replaceAll("&amp;", "&");

  let variables = findAllVariables(content);
  setTimeout(async () => {
    await replaceAsyncVariables({ patientDocument, variables });
  }, 20);

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const spans = doc.querySelectorAll(".documentVariable");
  for (let span of Array.from(spans)) {
    const variable = decodeAndParseBase64(
      span.getAttribute("data-value")
    ) as DocumentVariable;
    if (!variable) continue;

    let replacement: string | null | undefined;

    if (variable.source == "condition") {
      let conditionResult = evalConditionGroup({
        patientDocument,
        group: variable.conditionGroup,
      });

      replacement = replaceVariablesWithValues({
        patientDocument,
        content: conditionResult
          ? variable?.conditionReplacement || ""
          : variable?.conditionElseReplacement || "",
      });

      replacement = formatValue({
        patientDocument,
        variable,
        value: replacement,
        format: variable.format,
      });
    } else if (variable.source == "simpleHtml") {
      replacement = replaceVariablesWithValues({
        patientDocument,
        content: variable.value || "",
      });

      replacement = formatValue({
        patientDocument,
        variable,
        value: replacement,
        format: variable.format,
      });
    } else if (variable.source == "statistics") {
      replacement = `<div class="asyncVariable bold" data-value="${encodeBase64(
        variable
      )}">En attente...</div>`;
    } else if (variable.source == "manualVariables") {
      replacement = `<div class="asyncVariable bold" data-value="${encodeBase64(
        variable
      )}">En attente...</div>`;
    } else {
      let value = getVariableValue({
        variable,
        patientDocument,
      });

      let formattedValue = formatValue({
        patientDocument,
        variable,
        value,
        format: variable.format,
      });

      replacement = `${formattedValue}`;
    }

    if (isPlaceholder) replacement = `${variable.name}`;

    span.outerHTML = replacement || "";
  }

  return doc.body.innerHTML;
}

function getVariableSpan(variable: DocumentVariable) {
  let encoded = encodeBase64(variable);
  return `<span class="documentVariable" contenteditable="false" data-value="${encoded}">${variable.name}</span>`;
}

async function replaceAsyncVariables({
  patientDocument,
  variables,
}: {
  patientDocument: PatientDocument | null | undefined;
  variables: DocumentVariable[] | null | undefined;
}) {
  let count = 0;
  for (let variable of variables || []) {
    count++;
    let encoded = encodeBase64(variable);
    let $element = document.querySelectorAll(
      `.asyncVariable[data-value="${encoded}"]`
    )?.[0];

    if (!$element) continue;

    if (!variable) continue;
    if (variable.source == "manualVariables") {
      let value = await getManualVariableValue({
        variableName: variable.value as ManualVariable,
        item: patientDocument?.parent,
      });
      $element.innerHTML = `${value || ""}`;
      continue;
    }

    if (variable.source == "statistics") {
      let report = variable.statisticsReport;

      if (!report) continue;
      $element.innerHTML = `<div class="asyncVariable bold"  contenteditable="false" data-value="${encoded}">
      <img src="/images/loading-blue.gif" style="width: 20px; height: 20px; margin-right: 10px;"/>
      </div>`;

      report = deepReplaceVariables({
        patientDocument,
        value: report,
        isPlaceholder: false,
      }) as StatisticsReport;

      try {
        let res = await apiFetch("statistics", "calculateStatisticsReport", {
          report,
          shouldSave: false,
        });

        $element.innerHTML = `<div class="asyncVariable bold" contenteditable="false" data-value="${encoded}">${
          res.males + res.females
        }</div>`;
      } catch (e) {
        $element.innerHTML = `<div class="asyncVariable bold" contenteditable="false" data-value="${encoded}">Erreur</div>`;
      }
    }
  }
}

function deepReplaceVariables({
  patientDocument,
  value,
  isPlaceholder,
}: {
  patientDocument: PatientDocument | null | undefined;
  value: any;
  isPlaceholder: boolean;
}): any {
  if (Array.isArray(value)) {
    return value.map((val) =>
      deepReplaceVariables({ patientDocument, value: val, isPlaceholder })
    );
  }

  if (typeof value == "object") {
    let newValue = { ...value };
    for (let key in newValue) {
      newValue[key] = deepReplaceVariables({
        patientDocument,
        value: newValue[key],
        isPlaceholder,
      });
    }
    return newValue;
  }

  if (typeof value == "string") {
    if (!value?.includes("span")) return value;
    return replaceVariablesWithValues({
      patientDocument,
      content: value,
      isPlaceholder,
    });
  }
  return value;
}

function findAllVariables(text: string): DocumentVariable[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");
  const spans = doc.querySelectorAll(".documentVariable");
  const variables: DocumentVariable[] = [];
  for (let span of Array.from(spans)) {
    const variable = decodeAndParseBase64(
      span.getAttribute("data-value")
    ) as DocumentVariable;
    if (!variable) continue;
    variables.push(variable);
  }

  return variables;
}

function evalConditionGroup({
  patientDocument,
  group,
}: {
  patientDocument: PatientDocument | null | undefined;
  group: ConditionGroup | null | undefined;
}): boolean {
  if (!group) return false;
  if (group.type == "and") {
    let result = true;
    for (let condition of group.conditions || []) {
      let conditionResult = true;
      if (condition.category == "conditionGroup")
        conditionResult = evalConditionGroup({
          patientDocument,
          group: condition,
        });
      else
        conditionResult = evalCondition({
          patientDocument,
          condition,
        });
      if (!conditionResult) result = false;
    }
    return result;
  }

  if (group.type == "or") {
    let result = false;
    for (let condition of group.conditions || []) {
      let conditionResult = true;
      if (condition.category == "conditionGroup")
        conditionResult = evalConditionGroup({
          patientDocument,
          group: condition,
        });
      else
        conditionResult = evalCondition({
          patientDocument,
          condition,
        });
      if (conditionResult) result = true;
    }
    return result;
  }
  return false;
}
function evalCondition({
  patientDocument,
  condition,
}: {
  patientDocument: PatientDocument | null | undefined;
  condition: DocumentCondition;
}): boolean {
  let operator = conditionOperators[condition?.operator];
  if (!operator) return false;
  let a = replaceVariablesWithValues({
    content: condition?.a || "",
    patientDocument,
  })
    ?.trim()
    ?.replace("<span></span>", "");

  let b = replaceVariablesWithValues({
    content: condition?.b || "",
    patientDocument,
  })
    ?.trim()
    ?.replace("<span></span>", "");

  console.log("evalCondition", { a, b });
  try {
    return operator?.exec(a, b);
  } catch (e) {
    console.log("evalCondition error", {
      a,
      b,
      operator,
    });
    return false;
  }
}

function getVariableValue({
  variable,
  patientDocument,
}: {
  variable: DocumentVariable;
  patientDocument: PatientDocument | null | undefined;
}) {
  let source = variable?.source;
  let fieldTitle = getFieldTitle(patientDocument, variable?.fieldId || "");
  let fieldValue = getFieldValue(patientDocument, variable?.fieldId || "");

  if (source == "section") {
    let sectionId = variable?.sectionId;
    let section = patientDocument?.refAnswer?.reference?.sections?.find(
      (item) => item?.id == sectionId
    );

    let htmlArr = section?.fields?.map((field) => {
      if (!field) return;
      if (!field.id) return;
      let fieldTitle = getFieldTitle(patientDocument, field.id);
      let fieldValue = getFieldValue(patientDocument, field.id);
      return `${fieldTitle} : <strong>${fieldValue}</strong> <br/>`;
    });
    return htmlArr?.join("") || "";
  }

  if (source == "fieldTitle") return fieldTitle;
  if (source == "fieldValue") {
    if (fieldValue) console.log("fieldValue", fieldValue);
    return fieldValue;
  }
  if (source == "patient")
    return patientDocument?.patient?.[variable?.value as keyof Patient];
  if (source == "patientDocument")
    return patientDocument?.[variable?.value as keyof PatientDocument];

  if (source == "user") {
    let user = patientDocument?.creator || USER?.get();
    return user?.[variable?.value as keyof User];
  }

  return fieldValue;
}

function getFieldTitle(
  patientDocument: PatientDocument | null | undefined,
  fieldId: string
) {
  let fieldAnswers = patientDocument?.refAnswer?.fieldAnswers || [];
  let fieldAnswer = fieldAnswers?.find((item) => item?.field?.id == fieldId);
  let field = fieldAnswer?.field;
  return field?.name;
}

function getFieldValue(
  patientDocument: PatientDocument | null | undefined,
  fieldId: string
) {
  let fieldAnswers = patientDocument?.refAnswer?.fieldAnswers || [];
  let fieldAnswer = fieldAnswers?.find((item) => item?.field?.id == fieldId);
  let fieldValue = fieldAnswer?.value;

  let optionValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
  let optionsStrings = [];
  for (let optionValue of optionValues) {
    if (!optionValue) continue;
    let option = fieldAnswer?.field?.options?.find(
      (option) => option?.id == optionValue
    );
    if (!option) continue;
    if (!option?.name) continue;
    optionsStrings.push(option?.name);
  }
  if (optionsStrings?.length) fieldValue = optionsStrings?.join(", ");

  let unit = fieldAnswer?.field?.unit?.name;
  return [fieldValue, unit].join(" ");
}

function formatValue({
  patientDocument,
  variable,
  value,
  format,
}: {
  patientDocument: PatientDocument | null | undefined;
  variable: DocumentVariable;
  value: any;
  format: DocumentVariableFormat;
}) {
  let formatInfo = documentVariableFormats[format];
  console.log("value", value);

  if (value === null || value === undefined || value === "")
    return replaceWithNullValue(patientDocument, variable, value);
  if (!formatInfo) return value;
  try {
    return replaceWithNullValue(
      patientDocument,
      variable,
      formatInfo?.exec(value)
    );
  } catch (e) {
    console.log("error in formatValue", {
      value,
      variable,
      formatInfo,
    });
    return value;
  }
}

function replaceWithNullValue(
  patientDocument: PatientDocument | null | undefined,
  variable: DocumentVariable,
  value: any
) {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    `${value}`?.trim() == "" ||
    `${value}`.toLowerCase() === "invalid date"
  )
    return variable?.nullValue
      ? replaceVariablesWithValues({
          patientDocument,
          content: variable?.nullValue,
          isPlaceholder: false,
        })
      : value;

  return value;
}
function VariableOptions({
  sections,
  currentVariable,
  onChange,
}: {
  sections: (FieldsSection | null | undefined)[] | null | undefined;
  currentVariable: DocumentVariable;
  onChange?: (newVariable: DocumentVariable) => void;
}) {
  const [variable, setVariable] = useState(currentVariable);

  function onVariableChange(variable: DocumentVariable) {
    onChange?.({ ...variable });
    setVariable({ ...variable });
  }

  return (
    <div className="c1 gap10">
      <Input
        value={variable?.format}
        name="Format"
        type="select"
        options={Object.values(documentVariableFormats)?.map((format) => {
          return { value: format.id, label: format.name };
        })}
        onChange={(newValue) => {
          variable.format = newValue;
          onVariableChange(variable);
        }}
      ></Input>

      <VariableInputPicker
        name={"Valeur si nulle"}
        value={variable?.nullValue || ""}
        sections={sections || []}
        toolbarId={uid()}
        onChange={(newValue) => {
          variable.nullValue = newValue;
          onChange?.({ ...variable });
          // onVariableChange(variable);
        }}
      ></VariableInputPicker>
    </div>
  );
}

export {
  VariableOptions,
  deepReplaceVariables,
  evalConditionGroup,
  findAllVariables,
  getVariableSpan,
  replaceVariablesWithValues,
};
